/**
=========================================================
* Trust Cargo - accounting: talking to the server
=========================================================

* Every accounting route sits behind sign-in, so every call carries the
* session's token - the same rule as Backups. Every call is also counted
* while it is on its way, so the page can show it is working (useWorking).
*/

import { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";

export const API = "https://trust-cargo.co/anas_backend/api/v2/accounting";

export const CURRENCIES = ["CNY", "USD", "ILS", "JOD", "EUR", "AED"];

/** How each document is numbered on screen and on paper. */
export const PREFIX = {
  receipt: "RV-",
  payment: "PV-",
  sales: "SI-",
  purchase: "PI-",
  journal: "JV-",
};

export const docNo = (type, number) => `${PREFIX[type] || ""}${number}`;

const headers = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
});

/** What a failed call means for the person looking at the screen. */
export const problemOf = (e, fallback = "Something went wrong") => {
  const status = e?.response?.status;
  if (status === 401) return "انتهت جلسة الدخول. سجّل خروج وادخل من جديد عشان تستخدم المحاسبة.";
  // the server says which permission is missing
  if (status === 403) return e?.response?.data?.message || "ما عندك صلاحية لهاي العملية.";
  // Laravel's validation answer: show the first field's message
  const errors = e?.response?.data?.errors;
  if (errors) return Object.values(errors).flat()[0];
  return e?.response?.data?.message || e?.message || fallback;
};

/* calls still on their way, and who wants to know when that changes */
let working = 0;
const watchers = new Set();
const tell = () => watchers.forEach((w) => w(working > 0));

const counted = (call) => {
  working += 1;
  tell();
  return call
    .then((r) => r.data)
    .finally(() => {
      working -= 1;
      tell();
    });
};

/** true while any accounting call is on its way. */
export function useWorking() {
  const [on, setOn] = useState(working > 0);
  useEffect(() => {
    watchers.add(setOn);
    setOn(working > 0);
    return () => watchers.delete(setOn);
  }, []);
  return on;
}

export const api = {
  get: (path, params) => counted(axios.get(`${API}${path}`, { headers: headers(), params })),
  post: (path, body) => counted(axios.post(`${API}${path}`, body, { headers: headers() })),
  put: (path, body) => counted(axios.put(`${API}${path}`, body, { headers: headers() })),
  del: (path) => counted(axios.delete(`${API}${path}`, { headers: headers() })),
};

export const money = (v) =>
  v === null || v === undefined || v === ""
    ? "-"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const today = () => new Date().toISOString().slice(0, 10);

/** Adds amounts up per currency - CNY and USD are never summed together. */
export function byCurrency(rows, amountOf, currencyOf = (r) => r.currency) {
  const out = {};
  (rows || []).forEach((r) => {
    const c = currencyOf(r) || "-";
    out[c] = (out[c] || 0) + (Number(amountOf(r)) || 0);
  });
  return out;
}

/** "1,200.00 CNY · 300.00 USD" */
export const currencyLine = (totals) =>
  Object.keys(totals).length
    ? Object.entries(totals)
        .map(([c, v]) => `${money(v)} ${c}`)
        .join(" · ")
    : "0";

const party = PropTypes.shape({
  id: PropTypes.number,
  code: PropTypes.string,
  name: PropTypes.string,
});

/** The lists loaded once on the page and handed to every tab. */
export const LOOKUPS = PropTypes.shape({
  boxes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  categories: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  parties: PropTypes.shape({
    customers: PropTypes.arrayOf(party),
    suppliers: PropTypes.arrayOf(party),
    vendors: PropTypes.arrayOf(party),
  }),
});
